import { cn } from "@/lib/utils/cn";
import React from "react";
import { motion } from "motion/react";
import { Button } from "@/ui-components/ui/button";
import { ArrowRight, X } from "lucide-react";
import { Link } from "react-router-dom";

interface StoreCtaBannerProps {
  className?: string;
  onClose?: () => void;
}

/**
 * A modern, engaging banner to promote the Template Store.
 */
export const StoreCtaBanner = ({ className, onClose }: StoreCtaBannerProps) => {
  const [isExiting, setIsExiting] = React.useState(false);

  const handleClose = () => {
    setIsExiting(true);
    setTimeout(() => {
      onClose?.();
    }, 300);
  };

  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-[24px] p-8 md:p-10 shadow-lg bg-gradient-to-br from-blue-600 to-indigo-700 flex flex-col md:flex-row items-center justify-between gap-8 min-h-[240px]",
        isExiting
          ? "animate-out fade-out zoom-out-95 slide-out-to-top-2 duration-300 fill-mode-forwards"
          : "animate-in fade-in zoom-in-95 slide-in-from-top-2 duration-300",
        className
      )}
    >
      {onClose && (
        <button
          type="button"
          onClick={handleClose}
          className="absolute right-4 top-4 z-30 flex h-9 w-9 items-center justify-center rounded-xl bg-white/10 text-white/70 transition-colors hover:bg-white/15 hover:text-white"
          title="Schließen"
        >
          <X className="h-4 w-4" />
        </button>
      )}

      {/* Decorative background elements */}
      <div className="absolute top-0 right-0 w-64 h-64 rounded-full -mr-24 -mt-24 blur-3xl bg-white/10 pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-48 h-48 rounded-full -ml-16 -mb-16 blur-2xl bg-blue-400/20 pointer-events-none" />

      {/* Light Sweep Shine Animation */}
      <motion.div
        className="absolute inset-0 z-20 pointer-events-none"
        initial={{ x: "-100%", opacity: 0 }}
        animate={{ x: "200%", opacity: 1 }}
        transition={{
          duration: 3,
          ease: "easeInOut",
          delay: 1,
          repeat: Infinity,
          repeatDelay: 5,
        }}
      >
        <div className="w-1/2 h-full bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-[30deg] transform" />
      </motion.div>

      {/* Content */}
      <div className="relative z-10 flex-1 flex flex-col items-start min-w-0">
        {/* <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-md px-3 py-1.5 rounded-full mb-3 shadow-sm ring-1 ring-white/30">
          <Sparkles className="w-4 h-4 text-white" />
          <span className="text-white text-xs font-semibold uppercase tracking-wider">
            Neue Templates im Store
          </span>
        </div> */}
        <h2 className="text-2xl md:text-3xl font-bold text-white mb-2 tracking-tight line-clamp-1">
           Zeit für neue Designs?
        </h2>
        <p className="text-blue-100 font-medium text-sm md:text-base mb-6 line-clamp-2 max-w-xl">
           Entdecke viele professionelle Templates für Social Media und bringe die Content-Qualität deines Vereins auf das nächste Level.
        </p>
        <Link to="/post-generator/store" className="mt-2" data-onboarding="post-generator-start-store-cta">
          <Button
            className="group bg-white hover:bg-gray-50 text-blue-700 font-bold rounded-xl shadow-xl shadow-blue-900/20 transition-all hover:scale-105 active:scale-95"
            size="lg"
          >
            Zum Template Store
            <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
          </Button>
        </Link>
      </div>

      {/* Decorative Images (Examples) */}
      <div className="relative z-10 hidden md:flex shrink-0 w-[300px] h-full items-center justify-center translate-x-4">
        {/* Template 1 */}
        <div className="absolute right-16 z-10 w-[140px] h-auto shadow-2xl rotate-[-6deg] overflow-hidden rounded-xl border-4 border-white opacity-90 transition-all duration-500 ease-[cubic-bezier(0.19,1,0.22,1)] hover:z-30 hover:scale-[1.15] hover:rotate-0 hover:opacity-100 cursor-pointer">
           <img 
              src="/assets/examples/template1.png" 
              alt="Template Preview 1" 
              className="w-full h-auto object-cover bg-gray-100" 
              onError={(e) => { e.currentTarget.style.display = 'none'; }} 
           />
        </div>

        {/* Template 2 */}
        <div className="absolute right-0 z-20 w-[160px] h-auto shadow-2xl rotate-[6deg] overflow-hidden rounded-xl border-4 border-white group transition-all duration-500 ease-[cubic-bezier(0.19,1,0.22,1)] hover:z-30 hover:scale-[1.15] hover:rotate-0 cursor-pointer">
           <img 
              src="/assets/examples/template2.png" 
              alt="Template Preview 2" 
              className="w-full h-auto object-cover bg-gray-100" 
              onError={(e) => { e.currentTarget.style.display = 'none'; }} 
           />
        </div>
      </div>
    </div>
  );
};
